import type { AxiosPromise } from 'axios';
import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { CatalogImportsEndpoints } from './catalogImportsEndpoints';
import {
    toCatalogImportListPage,
    toCatalogImportRecord,
    toCatalogImportResult,
    toCatalogImportUndoResult,
} from './catalogImportsMapping';
import type { ICatalogImportsService } from './ICatalogImportsService';
import type { CatalogImportListQuery, CatalogImportRequest } from './requests';
import type {
    CatalogImportLinesInvalidError,
    CatalogImportListPageWire,
    CatalogImportListResponse,
    CatalogImportPreviewResponse,
    CatalogImportRecordWire,
    CatalogImportResponse,
    CatalogImportResultWire,
    CatalogImportUndoResponse,
    CatalogImportUndoWire,
} from './responses';

type CatalogImportsServiceOptions = {
    baseUrl: string;
};

/** The refusal body's `result`, when the server sent one. */
const resultOf = (body: unknown): CatalogImportResultWire | null => {
    const result =
        typeof body === 'object' && body !== null && 'result' in body
            ? body.result
            : null;
    return typeof result === 'object' && result !== null
        ? (result as CatalogImportResultWire)
        : null;
};

export class CatalogImportsService implements ICatalogImportsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: CatalogImportsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public async preview(
        request: CatalogImportRequest,
    ): Promise<ApiResponse<CatalogImportPreviewResponse>> {
        const result = await makeApiRequest<CatalogImportResultWire>(
            getAxiosClient({ baseURL: this.baseUrl }).post(
                CatalogImportsEndpoints.preview(),
                request,
            ),
        );
        if (!result.success) {
            return result;
        }
        return {
            success: true,
            response: toCatalogImportResult(result.response),
        };
    }

    public async import(
        request: CatalogImportRequest,
    ): Promise<ApiResponse<CatalogImportResponse>> {
        // `makeApiRequest` keeps only the envelope's known fields, and the one
        // refusal that matters here carries the whole per-row result beside
        // them. The rejected body is kept on the way through so that refusal
        // can be handed back with its result mapped.
        let refusedBody: unknown = null;
        const post: AxiosPromise = getAxiosClient({
            baseURL: this.baseUrl,
        })
            .post(CatalogImportsEndpoints.import(), request)
            .catch((error: unknown) => {
                refusedBody = (error as { response?: { data?: unknown } })
                    ?.response?.data;
                throw error;
            });

        const result = await makeApiRequest<CatalogImportRecordWire>(post);
        if (result.success) {
            return {
                success: true,
                response: toCatalogImportRecord(result.response),
            };
        }

        const refusedResult = resultOf(refusedBody);
        if (result.error.code === 'IMPORT_LINES_INVALID' && refusedResult) {
            const error: CatalogImportLinesInvalidError = {
                ...result.error,
                code: 'IMPORT_LINES_INVALID',
                result: toCatalogImportResult(refusedResult),
            };
            return { success: false, error };
        }
        return result;
    }

    public async getList(
        query: CatalogImportListQuery,
    ): Promise<ApiResponse<CatalogImportListResponse>> {
        // No branch means every branch (OrgAdmin only): the filter is left
        // out. The server reads a blank `branchId=` the same way, but an
        // absent parameter says so without relying on it.
        const params = {
            ...(query.branchId ? { branchId: query.branchId } : {}),
            page: query.page,
            pageSize: query.pageSize,
        };
        const result = await makeApiRequest<CatalogImportListPageWire>(
            getAxiosClient({ baseURL: this.baseUrl }).get(
                CatalogImportsEndpoints.list(),
                { params },
            ),
        );
        if (!result.success) {
            return result;
        }
        return {
            success: true,
            response: toCatalogImportListPage(result.response),
        };
    }

    public async getById(
        id: string,
    ): Promise<ApiResponse<CatalogImportResponse>> {
        const result = await makeApiRequest<CatalogImportRecordWire>(
            getAxiosClient({ baseURL: this.baseUrl }).get(
                CatalogImportsEndpoints.byId(id),
            ),
        );
        if (!result.success) {
            return result;
        }
        return {
            success: true,
            response: toCatalogImportRecord(result.response),
        };
    }

    public async undo(
        id: string,
    ): Promise<ApiResponse<CatalogImportUndoResponse>> {
        const result = await makeApiRequest<CatalogImportUndoWire>(
            getAxiosClient({ baseURL: this.baseUrl }).post(
                CatalogImportsEndpoints.undo(id),
            ),
        );
        if (!result.success) {
            return result;
        }
        return {
            success: true,
            response: toCatalogImportUndoResult(result.response),
        };
    }
}
